import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  concatMap,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  pairwise,
  pipe,
  startWith,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { CreateLegacyCustomElementStory$Params } from '../../api/core/fn/legacy-custom-element/create-legacy-custom-element-story';
import { CreateTransitionElementStory$Params } from '../../api/core/fn/transition-element/create-transition-element-story';
import { GetTransitionStoryGroupStory$Params } from '../../api/core/fn/transition-story-groups/get-transition-story-group-story';
import { UpdateTransitionStoryGroupStory$Params } from '../../api/core/fn/transition-story-groups/update-transition-story-group-story';
import {
  ApiLegacyCustomElementResponse,
  ApiListTransitionStoryGroupsResponse,
  ApiStoryResponse,
  ApiTransitionElementResponse,
  ApiTransitionStoryGroupResponse,
  ApiUpdateLegacyCustomElementRequest,
  ApiWatchlistResponse,
} from '../../api/core/models';
import {
  LegacyCustomElementService,
  TransitionElementService,
  TransitionStoryGroupsService,
  WatchlistService,
} from '../../api/core/services';
import { EMPTY_GUID } from '../../constants/empty-guid';
import { LegacyCustomElementData } from '../../feature/editor-transition-targets/components/transition-target-editor/transition-target-editor.component';
import { injectUntilDestroyed } from '../../functions/inject-functions/inject-until-destroyed';
import { UI_STRINGS } from '../../functions/language/uistrings';
import { sectorToString } from '../../functions/sector-to-string';
import {
  conditionallyPrefixImagesWithFilesUrl,
  conditionallyRemoveFilesUrlPrefixOnImages,
} from '../../functions/utilities/img-url-utility';
import { TransitionElement } from '../../model/enums/transition-element';
import { TransitionTargetSubsector } from '../../model/enums/transition-target-subsector';
import { Sector } from '../../model/sector';
import { AppConfigService } from '../../services/app-config.service';
import { BoardFacadeService } from '../../services/store/board/board-facade.service';
import { TranslationService } from '../../services/translation-service';
import { selectRouteBoardId } from '../router/router.selectors';
import { TransitionElementsStore } from '../transition-elements/transition-elements.store';

type TransitionStoriesState = {
  error: Error | HttpErrorResponse | undefined;
  isFullTransitionStoryHierarchy: boolean;
  isLoading: boolean;
  isSaving: boolean;
  transitionStoryHierarchy: ApiListTransitionStoryGroupsResponse | undefined;
  transitionHierarchyStories: Record<string, ApiStoryResponse>;
};

const initialState: TransitionStoriesState = {
  error: undefined,
  isFullTransitionStoryHierarchy: false,
  isLoading: false,
  isSaving: false,
  transitionStoryHierarchy: undefined,
  transitionHierarchyStories: {},
};

const toLocalisedString = (language: string, string?: string) =>
  typeof string === 'string' ? { [language]: string } : {};

export type CreateLegacyCustomElement = {
  title: string;
  transitionElement: TransitionElement;
  sector?: Sector;
  subsector?: TransitionTargetSubsector;
  lhsCccs?: string[];
};

@Injectable({ providedIn: 'root' })
export class TransitionStoriesStore extends signalStore(
  withState(initialState),
  withMethods((store) => {
    const boardFacadeService = inject(BoardFacadeService);
    const config = inject(AppConfigService);
    const legacyCustomElementsService = inject(LegacyCustomElementService);
    const store$ = inject(Store);
    const transitionStoryGroupsService = inject(TransitionStoryGroupsService);
    const transitionElementsService = inject(TransitionElementService);
    const transitionElementsStore = inject(TransitionElementsStore);
    const translationService = inject(TranslationService);
    const watchlistService = inject(WatchlistService);

    return {
      loadTransitionStoryHierarchy: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(() =>
            combineLatest([
              boardFacadeService.getBoardProperty$(['id']),
              boardFacadeService.boardLanguage$,
              store$.select(selectRouteBoardId),
            ]).pipe(
              filter(
                ([boardDataId, language, routeBoardId]) =>
                  Boolean(boardDataId) && Boolean(language) && Boolean(routeBoardId) && boardDataId === routeBoardId
              ),
              mergeMap(([_, language, boardId]) =>
                transitionStoryGroupsService.listTransitionStoryGroups({
                  boardId: boardId!,
                  'Accept-Language': language!,
                })
              ),
              tapResponse({
                next: (hierarchy) => {
                  patchState(store, {
                    transitionStoryHierarchy: hierarchy,
                    isFullTransitionStoryHierarchy: false,
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error loading transition story hierarchy:', error);
                },
              })
            )
          )
        )
      ),
      deleteTransitionStoryGroup: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap((id) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) => transitionStoryGroupsService.deleteTransitionStoryGroup({ boardId: boardId!, id })),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    const { newGroups } = _removeGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id
                    );
                    transitionStoryHierarchy.transitionStoryGroups = newGroups;
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      deleteLegacyCustomElement: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap((id) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) => legacyCustomElementsService.deleteLegacyCustomElement({ boardId: boardId!, id })),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    const { newGroups } = _removeGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id
                    );
                    transitionStoryHierarchy.transitionStoryGroups = newGroups;
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      deleteTransitionElement: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap((id) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) => transitionElementsService.deleteTransitionElement({ boardId: boardId!, id })),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    const { newGroups } = _removeGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id
                    );
                    transitionStoryHierarchy.transitionStoryGroups = newGroups;
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      loadTransitionStoryGroupStory: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          _getStory(
            boardFacadeService,
            store$,
            transitionStoryGroupsService.getTransitionStoryGroupStory.bind(transitionStoryGroupsService),
            store,
            config
          )
        )
      ),
      loadTransitionElementStory: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          _getStory(
            boardFacadeService,
            store$,
            transitionElementsService.getTransitionElementStory.bind(transitionElementsService),
            store,
            config
          )
        )
      ),
      loadLegacyCustomElementStory: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          _getStory(
            boardFacadeService,
            store$,
            legacyCustomElementsService.getLegacyCustomElementStory.bind(legacyCustomElementsService),
            store,
            config
          )
        )
      ),
      loadAllStories: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          mergeMap(() =>
            combineLatest([
              boardFacadeService.getBoardProperty$(['id']),
              boardFacadeService.boardLanguage$,
              store$.select(selectRouteBoardId),
            ]).pipe(
              filter(
                ([boardDataId, language, routeBoardId]) =>
                  Boolean(boardDataId) && Boolean(language) && Boolean(routeBoardId) && boardDataId === routeBoardId
              ),
              mergeMap(([_, language, boardId]) =>
                transitionStoryGroupsService.listTransitionStoryGroups({
                  includeAll: true,
                  boardId: boardId!,
                  'Accept-Language': language!,
                })
              ),
              tapResponse({
                next: (hierarchy) => {
                  patchState(store, {
                    isFullTransitionStoryHierarchy: true,
                    transitionStoryHierarchy: _expandUrlsForHierarchy(hierarchy, config.baseUrlFiles),
                    transitionHierarchyStories: _createTransitionHierarchyStories(hierarchy, config.baseUrlFiles),
                    isLoading: false,
                    error: undefined,
                  });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isLoading: false, error });
                  console.error('Error loading transition story hierarchy:', error);
                },
              })
            )
          )
        )
      ),
      moveLegacyCustomElement: rxMethod<{ id: string; targetGroupId: string; targetOrder: number }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ id, targetOrder, targetGroupId }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) =>
                legacyCustomElementsService.moveLegacyCustomElement({
                  boardId: boardId!,
                  id,
                  body: { targetOrder, targetGroupId },
                })
              ),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    const { newGroups, elementOrGroup } = _removeGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id
                    );
                    transitionStoryHierarchy.transitionStoryGroups = _addGroupOrElementRecursively(
                      newGroups,
                      id,
                      elementOrGroup!,
                      targetGroupId,
                      targetOrder
                    );
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      moveTransitionElement: rxMethod<{ id: string; targetGroupId: string; targetOrder: number }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ id, targetOrder, targetGroupId }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) =>
                transitionElementsService.moveTransitionElement({
                  boardId: boardId!,
                  id,
                  body: { targetOrder, targetGroupId },
                })
              ),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    const { newGroups, elementOrGroup } = _removeGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id
                    );
                    transitionStoryHierarchy.transitionStoryGroups = _addGroupOrElementRecursively(
                      newGroups,
                      id,
                      elementOrGroup!,
                      targetGroupId,
                      targetOrder
                    );
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      moveTransitionStoryGroup: rxMethod<{ id: string; targetOrder: number; targetParentGroupId?: string }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ id, targetOrder, targetParentGroupId }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) =>
                transitionStoryGroupsService.moveTransitionStoryGroup({
                  boardId: boardId!,
                  id,
                  body: { targetOrder, targetParentGroupId },
                })
              ),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    const { newGroups, elementOrGroup } = _removeGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id
                    );
                    transitionStoryHierarchy.transitionStoryGroups = _addGroupOrElementRecursively(
                      newGroups,
                      id,
                      elementOrGroup!,
                      targetParentGroupId,
                      targetOrder
                    );
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      updateTransitionStoryGroup: rxMethod<{ id: string; title: string }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ id, title }) =>
            combineLatest([store$.select(selectRouteBoardId), boardFacadeService.boardLanguage$]).pipe(
              filter(([boardId, language]) => Boolean(boardId) && Boolean(language)),
              take(1),
              mergeMap(([boardId, language]) =>
                transitionStoryGroupsService.updateTransitionStoryGroup({
                  boardId: boardId!,
                  id,
                  body: { title: toLocalisedString(language, title) },
                })
              ),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    transitionStoryHierarchy.transitionStoryGroups = _updateGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id,
                      title
                    );
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      updateTransitionStoryGroupColour: rxMethod<{ id: string; colour: string }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ id, colour }: { id: string; colour: string }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) => {
                colour = colour ?? '';
                return transitionStoryGroupsService.updateTransitionStoryGroupStory({
                  boardId: boardId!,
                  id,
                  body: { color: colour },
                });
              }),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    transitionStoryHierarchy.transitionStoryGroups = _updateGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id,
                      undefined,
                      colour
                    );
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      updateTransitionStoryGroupStory: rxMethod<{ id: string; story: ApiStoryResponse }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          _patchStory(
            boardFacadeService,
            store$,
            transitionStoryGroupsService.updateTransitionStoryGroupStory.bind(transitionStoryGroupsService),
            store,
            config
          )
        )
      ),
      updateTransitionElementStory: rxMethod<{ id: string; story: ApiStoryResponse }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          _patchStory(
            boardFacadeService,
            store$,
            transitionElementsService.updateTransitionElementStory.bind(transitionElementsService),
            store,
            config
          )
        )
      ),
      updateLegacyCustomElementStory: rxMethod<{ id: string; story: ApiStoryResponse }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          _patchStory(
            boardFacadeService,
            store$,
            legacyCustomElementsService.updateLegacyCustomElementStory.bind(legacyCustomElementsService),
            store,
            config
          )
        )
      ),
      updateLegacyCustomElementWatchlist: rxMethod<{
        id: string;
        watchlist: ApiWatchlistResponse;
        shouldDeleteWatchlist: boolean;
      }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ id, watchlist, shouldDeleteWatchlist }) =>
            store$.select(selectRouteBoardId).pipe(
              filter((boardId) => Boolean(boardId)),
              take(1),
              mergeMap((boardId) => {
                if (watchlist.id && watchlist.id !== EMPTY_GUID) {
                  if (shouldDeleteWatchlist) {
                    return watchlistService
                      .deleteWatchlist({
                        boardId: boardId!,
                        id: watchlist.id,
                      })
                      .pipe(map(() => ({})));
                  }
                  return watchlistService
                    .updateWatchlist({
                      boardId: boardId!,
                      id: watchlist.id,
                      body: {
                        // @ts-ignore
                        ...watchlist,
                        owner: 'legacy_custom_elements',
                        ownerId: id,
                      },
                    })
                    .pipe(map(() => watchlist));
                } else {
                  return watchlistService
                    .createWatchlist({
                      boardId: boardId!,
                      body: {
                        // @ts-ignore
                        ...watchlist,
                        owner: 'legacy_custom_elements',
                        ownerId: id,
                      },
                    })
                    .pipe(map((res) => ({ ...watchlist, id: res.id })));
                }
              }),
              tapResponse({
                next: (response) => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    transitionStoryHierarchy.transitionStoryGroups = _updateGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id,
                      undefined,
                      undefined,
                      <any>response
                    );
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      updateLegacyCustomElement: rxMethod<{ id: string; title?: string; data?: LegacyCustomElementData }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ id, title, data }) =>
            combineLatest([store$.select(selectRouteBoardId), boardFacadeService.boardLanguage$]).pipe(
              filter(([boardId, language]) => Boolean(boardId) && Boolean(language)),
              take(1),
              mergeMap(([boardId, language]) => {
                const body: ApiUpdateLegacyCustomElementRequest = {};
                if (title) {
                  body.title = toLocalisedString(language, title);
                }
                if (data) {
                  body.data = JSON.stringify(data);
                }
                return legacyCustomElementsService.updateLegacyCustomElement({
                  boardId: boardId!,
                  id,
                  body,
                });
              }),
              tapResponse({
                next: () => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    transitionStoryHierarchy.transitionStoryGroups = _updateGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      id,
                      title,
                      undefined,
                      undefined,
                      undefined,
                      data
                    );
                    patchState(store, { transitionStoryHierarchy });
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      createTransitionStoryGroup: rxMethod<{ newGroupId: string; parentGroupId?: string }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ parentGroupId, newGroupId }: { parentGroupId?: string; newGroupId: string }) =>
            boardFacadeService.boardLanguage$.pipe(
              filter(Boolean),
              take(1),
              // need to load the language, since translate$ will emit with fallback language first, if no translation is found
              mergeMap((language) => translationService.loadLanguageIfNeeded(language).pipe(map((_) => language))),
              mergeMap((language) =>
                combineLatest([
                  store$.select(selectRouteBoardId),
                  translationService.translate$({
                    language,
                    translationKey: UI_STRINGS.TRANSITION_HIERARCHY.NEW_GROUP,
                  }),
                ]).pipe(
                  filter(([boardId, newGroupTitle]) => Boolean(boardId) && Boolean(newGroupTitle)),
                  take(1),
                  mergeMap(([boardId, newGroupTitle]) =>
                    transitionStoryGroupsService
                      .createTransitionStoryGroup({
                        boardId: boardId!,
                        body: {
                          parentGroupId,
                          title: toLocalisedString(language, newGroupTitle),
                        },
                      })
                      .pipe(map((group) => ({ ...group, title: newGroupTitle })))
                  ),
                  tapResponse({
                    next: (group) => {
                      const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                      if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                        transitionStoryHierarchy.transitionStoryGroups = _addGroupOrElementRecursively(
                          transitionStoryHierarchy.transitionStoryGroups,
                          newGroupId,
                          group as ApiTransitionStoryGroupResponse,
                          parentGroupId,
                          undefined
                        );
                        patchState(store, { transitionStoryHierarchy });
                      }
                      patchState(store, { isSaving: false, error: undefined });
                    },
                    error: (error: Error | HttpErrorResponse) => {
                      patchState(store, { isSaving: false, error });
                      console.error('Error creating transition story group:', error);
                    },
                  })
                )
              )
            )
          )
        )
      ),
      createTransitionStoryGroupStory: rxMethod<{ id: string; story: ApiStoryResponse }>(
        pipe(
          tap(() => console.warn('createTransitionStoryGroupStory not implemented'))
          // tap(() => patchState(store, { isSaving: true })),
          // createStory(boardFacadeService, store$, transitionStoryGroupsService.create.bind(transitionStoryGroupsService), nodesStore, store, config)
        )
      ),
      createElements: rxMethod<{
        parentGroupId: string;
        transitionElements: { title: string; transitionElement: TransitionElement }[];
        legacyCustomElements: CreateLegacyCustomElement[];
      }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          mergeMap(({ parentGroupId, transitionElements, legacyCustomElements }) =>
            combineLatest([store$.select(selectRouteBoardId), boardFacadeService.boardLanguage$]).pipe(
              filter(Boolean),
              take(1),
              mergeMap(
                ([boardId, language]): Observable<ApiTransitionElementResponse | ApiLegacyCustomElementResponse> => {
                  // sequential creation of transition elements and legacy custom elements
                  const transitionElementsToCreate = transitionElements.map((te) => ({ type: 'TE', obj: te }));
                  const legacyCustomElementsToCreate = legacyCustomElements.map((lce) => ({
                    type: 'LCE',
                    obj: lce,
                  }));
                  return from([...transitionElementsToCreate, ...legacyCustomElementsToCreate]).pipe(
                    concatMap(({ type, obj }) => {
                      patchState(store, { isSaving: true });
                      if (type === 'TE') {
                        return transitionElementsService
                          .createTransitionElement({
                            boardId: boardId!,
                            body: {
                              transitionStoryGroupId: parentGroupId,
                              transitionElement: obj.transitionElement,
                            },
                          })
                          .pipe(map((te) => ({ ...te, actionIds: [], progress: [] })));
                      } else {
                        return legacyCustomElementsService
                          .createLegacyCustomElement({
                            boardId: boardId!,
                            body: {
                              transitionStoryGroupId: parentGroupId,
                              linkedTransitionElement: obj.transitionElement,
                              data: JSON.stringify({
                                sector:
                                  typeof (obj as CreateLegacyCustomElement).sector === 'string'
                                    ? (obj as CreateLegacyCustomElement).sector
                                    : sectorToString((obj as CreateLegacyCustomElement).sector!),
                                subsector: (obj as CreateLegacyCustomElement).subsector,
                                lhsCccs: (obj as CreateLegacyCustomElement).lhsCccs,
                              }),
                              title: (obj as CreateLegacyCustomElement).transitionElement
                                ? undefined
                                : toLocalisedString(language, (obj as CreateLegacyCustomElement).title),
                            },
                          })
                          .pipe(
                            map((legacyCustomElement) => ({
                              ...legacyCustomElement,
                              title: (obj as CreateLegacyCustomElement).transitionElement
                                ? undefined
                                : (obj as CreateLegacyCustomElement).title,
                            }))
                          );
                      }
                    })
                  );
                }
              ),
              tapResponse({
                next: (element) => {
                  const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
                  if (transitionStoryHierarchy && transitionStoryHierarchy.transitionStoryGroups) {
                    transitionStoryHierarchy.transitionStoryGroups = _addGroupOrElementRecursively(
                      transitionStoryHierarchy.transitionStoryGroups,
                      element.id,
                      element,
                      parentGroupId,
                      undefined
                    );
                    patchState(store, { transitionStoryHierarchy });
                    if ((<ApiTransitionElementResponse>element).transitionElement) {
                      transitionElementsStore.addTransitionElement(<ApiTransitionElementResponse>element);
                    }
                  }
                  patchState(store, { isSaving: false, error: undefined });
                },
                error: (error: Error | HttpErrorResponse) => {
                  patchState(store, { isSaving: false, error });
                },
              })
            )
          )
        )
      ),
      createTransitionElementStory: rxMethod<{ id: string; story: ApiStoryResponse }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          switchMap(({ id, story }) =>
            _createStory(
              boardFacadeService,
              store$,
              transitionElementsService.createTransitionElementStory.bind(transitionElementsService),
              config,
              story,
              id
            ).pipe(_patchStoreWithStory(store, id, story))
          )
        )
      ),
      createLegacyCustomElementStory: rxMethod<{ id: string; story: ApiStoryResponse }>(
        pipe(
          tap(() => patchState(store, { isSaving: true })),
          switchMap(({ id, story }) =>
            _createStory(
              boardFacadeService,
              store$,
              legacyCustomElementsService.createLegacyCustomElementStory.bind(legacyCustomElementsService),
              config,
              story,
              id
            ).pipe(_patchStoreWithStory(store, id, story))
          )
        )
      ),
      // Patch the store with action connections for transition elements and legacy custom elements
      patchElementsWithActionConnections: rxMethod<{
        elementIds: string[];
        actionId: string;
      }>(
        pipe(
          map(({ elementIds, actionId }) => {
            const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
            if (transitionStoryHierarchy) {
              const updateElementActions = (group: ApiTransitionStoryGroupResponse) => {
                // Update transition elements
                if (group.transitionElements?.length) {
                  group.transitionElements = group.transitionElements.map((element) => {
                    if (elementIds.includes(element.id)) {
                      const actionIds = new Set(element.actionIds || []);
                      actionIds.add(actionId);
                      return {
                        ...element,
                        actionIds: Array.from(actionIds),
                      };
                    }
                    // Remove actionId from transition element if it is not in the elementIds array
                    return {
                      ...element,
                      actionIds: element.actionIds?.filter((id) => id !== actionId),
                    };
                  });
                }

                // Update legacy custom elements
                if (group.legacyCustomElements?.length) {
                  group.legacyCustomElements = group.legacyCustomElements.map((element) => {
                    if (elementIds.includes(element.id)) {
                      const actionIds = new Set(element.actionIds || []);
                      actionIds.add(actionId);
                      return {
                        ...element,
                        actionIds: Array.from(actionIds),
                      };
                    }
                    // Remove actionId from legacy custom element if it is not in the elementIds array
                    return {
                      ...element,
                      actionIds: element.actionIds?.filter((id) => id !== actionId),
                    };
                  });
                }

                // Recursively update subgroups
                if (group.subTransitionStoryGroups) {
                  group.subTransitionStoryGroups.forEach((subGroup) => {
                    updateElementActions(subGroup);
                  });
                }
              };

              transitionStoryHierarchy.transitionStoryGroups?.forEach((group) => {
                updateElementActions(group);
              });
            }
            return transitionStoryHierarchy;
          }),
          tap((hierarchy) => {
            patchState(store, {
              transitionStoryHierarchy: hierarchy,
              error: undefined,
            });
          })
        )
      ),
      setHierarchyAndStories: rxMethod<{
        hierarchy: ApiListTransitionStoryGroupsResponse;
        stories: Record<string, ApiStoryResponse>;
      }>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          tap(({ hierarchy, stories }) => {
            patchState(store, {
              isFullTransitionStoryHierarchy: true,
              transitionStoryHierarchy: hierarchy,
              transitionHierarchyStories: stories,
              isLoading: false,
              error: undefined,
            });
          })
        )
      ),
    };
  }),
  withHooks({
    onInit: (store) => {
      const untilDestroyed = injectUntilDestroyed();
      inject(Store)
        .select(selectRouteBoardId)
        .pipe(
          untilDestroyed(),
          startWith(undefined),
          pairwise(),
          filter(([prev, curr]) => prev !== undefined && prev !== curr)
        )
        .subscribe(() => {
          // resetting the state on board change
          patchState(store, initialState);
        });
    },
  })
) {}

function _createStory(
  boardFacadeService: BoardFacadeService,
  store$: Store<any>,
  createStoryFn: (
    params: CreateTransitionElementStory$Params | CreateLegacyCustomElementStory$Params
  ) => Observable<string>,
  config: AppConfigService,
  story: ApiStoryResponse,
  id: string
) {
  return combineLatest([boardFacadeService.boardLanguage$, store$.select(selectRouteBoardId)]).pipe(
    filter(([language, boardId]) => Boolean(language) && Boolean(boardId)),
    take(1),
    switchMap(([language, boardId]) =>
      createStoryFn({
        boardId: boardId!,
        id,
        body: {
          title: toLocalisedString(language, story.title),
          description: toLocalisedString(language, story.description),
          diagramDescription: toLocalisedString(language, story.diagramDescription),
          diagramHeader: toLocalisedString(language, story.diagramHeader),
          historicalEmissions: story.historicalEmissions ? story.historicalEmissions : {},
          imageCredits: toLocalisedString(language, story.imageCredits),
          color: story.color,
          imgUrl: story.imgUrl ? conditionallyRemoveFilesUrlPrefixOnImages(story.imgUrl, config.baseUrlFiles) : '',
        },
      })
    )
  );
}

function _getStory(
  boardFacadeService: BoardFacadeService,
  store$: Store<any>,
  getStoryFn: (
    params: GetTransitionStoryGroupStory$Params,
    context?: HttpContext | undefined
  ) => Observable<ApiStoryResponse>,
  store: any,
  config: AppConfigService
) {
  return switchMap((id: string) =>
    combineLatest([boardFacadeService.boardLanguage$, store$.select(selectRouteBoardId)]).pipe(
      filter(([language, boardId]) => Boolean(language) && Boolean(boardId)),
      take(1),
      mergeMap(([language, boardId]) =>
        getStoryFn({
          id,
          boardId: boardId!,
          'Accept-Language': language!,
        }).pipe(
          map((story) => ({
            ...story,
            imgUrl: story.imgUrl ? conditionallyPrefixImagesWithFilesUrl(story.imgUrl, config.baseUrlFiles) : '',
          }))
        )
      ),
      tapResponse({
        next: (story) => {
          patchState(store, {
            transitionHierarchyStories: {
              ...store.transitionHierarchyStories(),
              [id]: story,
            },
            isLoading: false,
            error: undefined,
          });
        },
        error: (error: Error | HttpErrorResponse) => {
          if (error instanceof HttpErrorResponse && error.status === 404) {
            const emptyStory: ApiStoryResponse = {
              id,
              title: '',
            };
            const transitionHierarchyStories: Record<string, ApiStoryResponse> = {
              ...store.transitionHierarchyStories(),
              [id]: emptyStory,
            };
            patchState(store, { transitionHierarchyStories });
          } else {
            patchState(store, { isLoading: false, error });
            console.error('Error loading story:', error);
          }
        },
      })
    )
  );
}

/**
 * Patches the transition stories store with a new story and updates the transition story hierarchy if needed.
 * For top level groups, also updates the color in the hierarchy.
 * @param store The transition stories store to patch
 * @param id ID of the story to patch
 * @param story The story data to patch with
 * @returns A tapResponse operator that handles the patch operation
 */

function _patchStoreWithStory(store: any, id: string, story: ApiStoryResponse) {
  return tapResponse({
    next: () => {
      const transitionStoryHierarchy = structuredClone(store.transitionStoryHierarchy());
      if (transitionStoryHierarchy) {
        // Update the color of the group, only applicable for top level groups
        transitionStoryHierarchy.transitionStoryGroups = _updateGroupOrElementRecursively(
          transitionStoryHierarchy.transitionStoryGroups,
          id,
          undefined,
          story.color,
          undefined,
          story
        );
      }
      patchState(store, {
        transitionHierarchyStories: {
          ...store.transitionHierarchyStories(),
          [id]: { ...story },
        },
        transitionStoryHierarchy,
        isSaving: false,
        error: undefined,
      });
    },
    error: (error: Error | HttpErrorResponse) => {
      console.error('Error:', error);
      patchState(store, { isSaving: false, error });
    },
  });
}

/**
 * Generic function to patch a story
 * Updates the transition stories state with the new story data.
 * @param boardFacadeService Service to get board properties
 * @param store$ NgRx store
 * @param apiCallFn API function to call
 * @param store Current transition stories store
 * @param config AppConfigService
 */

function _patchStory(
  boardFacadeService: BoardFacadeService,
  store$: Store<any>,
  apiCallFn: (params: UpdateTransitionStoryGroupStory$Params, context?: HttpContext | undefined) => Observable<string>,
  store: any,
  config: AppConfigService
) {
  return switchMap(({ id, story }: { id: string; story: ApiStoryResponse }) =>
    combineLatest([boardFacadeService.boardLanguage$, store$.select(selectRouteBoardId)]).pipe(
      filter(([language, boardId]) => Boolean(language) && Boolean(boardId)),
      take(1),
      switchMap(([language, boardId]) =>
        apiCallFn({
          boardId: boardId!,
          id,
          body: {
            title: toLocalisedString(language, story.title),
            description: toLocalisedString(language, story.description),
            diagramDescription: toLocalisedString(language, story.diagramDescription),
            diagramHeader: toLocalisedString(language, story.diagramHeader),
            historicalEmissions: story.historicalEmissions!,
            imageCredits: toLocalisedString(language, story.imageCredits),
            color: story.color ?? '',
            imgUrl: conditionallyRemoveFilesUrlPrefixOnImages(story.imgUrl!, config.baseUrlFiles),
          },
        })
      ),
      _patchStoreWithStory(store, id, story)
    )
  );
}

type RemoveGroupResult = {
  newGroups: ApiTransitionStoryGroupResponse[];
  elementOrGroup:
    | ApiTransitionStoryGroupResponse
    | ApiTransitionElementResponse
    | ApiLegacyCustomElementResponse
    | undefined;
};

/**
 * Removes a group or element from the transition story hierarchy.
 * @param groups The groups to remove the element from
 * @param id The ID of the element to remove
 * @returns The updated groups
 */
function _removeGroupOrElementRecursively(groups: ApiTransitionStoryGroupResponse[], id: string): RemoveGroupResult {
  return groups.reduce(
    (acc: RemoveGroupResult, group) => {
      if (group.id === id) {
        return { newGroups: acc.newGroups, elementOrGroup: group };
      }

      const updatedGroup = { ...group };
      if (updatedGroup.subTransitionStoryGroups?.length) {
        const { newGroups, elementOrGroup } = _removeGroupOrElementRecursively(
          updatedGroup.subTransitionStoryGroups,
          id
        );
        updatedGroup.subTransitionStoryGroups = newGroups;
        return { newGroups: [...acc.newGroups, updatedGroup], elementOrGroup: acc.elementOrGroup ?? elementOrGroup };
      }
      if (updatedGroup.legacyCustomElements?.length) {
        if (updatedGroup.sortOrder?.includes(id)) {
          const legacyCustomElement = updatedGroup.legacyCustomElements.find((element) => element.id === id);
          if (legacyCustomElement) {
            updatedGroup.sortOrder = updatedGroup.sortOrder?.filter((order) => order !== id);
            updatedGroup.legacyCustomElements = updatedGroup.legacyCustomElements.filter(
              (element) => element.id !== id
            );
            return { newGroups: [...acc.newGroups, updatedGroup], elementOrGroup: legacyCustomElement };
          }
        }
      }
      if (updatedGroup.transitionElements?.length) {
        if (updatedGroup.sortOrder?.includes(id)) {
          const transitionElement = updatedGroup.transitionElements.find((element) => element.id === id);
          if (transitionElement) {
            updatedGroup.sortOrder = updatedGroup.sortOrder?.filter((order) => order !== id);
            updatedGroup.transitionElements = updatedGroup.transitionElements.filter((element) => element.id !== id);
            return { newGroups: [...acc.newGroups, updatedGroup], elementOrGroup: transitionElement };
          }
        }
      }
      return { newGroups: [...acc.newGroups, updatedGroup], elementOrGroup: acc.elementOrGroup };
    },
    { newGroups: [], elementOrGroup: undefined }
  );
}

function _addGroupOrElementRecursively(
  groups: ApiTransitionStoryGroupResponse[],
  id: string,
  elementOrGroup: ApiTransitionStoryGroupResponse | ApiTransitionElementResponse | ApiLegacyCustomElementResponse,
  targetGroupId?: string,
  targetOrder?: number
): ApiTransitionStoryGroupResponse[] {
  if (!targetGroupId) {
    // Add to top level if no target group specified
    groups.splice(targetOrder ?? groups.length, 0, elementOrGroup as ApiTransitionStoryGroupResponse);
    return groups;
  }

  return groups.reduce((acc: ApiTransitionStoryGroupResponse[], group: ApiTransitionStoryGroupResponse) => {
    if (group.id === targetGroupId) {
      let isElement = false;
      if (!group.sortOrder) {
        group.sortOrder = [];
      }
      if ((elementOrGroup as ApiTransitionElementResponse).transitionElement) {
        isElement = true;
        group.sortOrder.splice(targetOrder ?? group.sortOrder.length, 0, elementOrGroup.id);
        if (!group.transitionElements) {
          group.transitionElements = [];
        }
        group.transitionElements.splice(
          targetOrder ?? group.transitionElements.length,
          0,
          elementOrGroup as ApiTransitionElementResponse
        );
      } else if ((elementOrGroup as ApiLegacyCustomElementResponse).data) {
        isElement = true;
        group.sortOrder.splice(targetOrder ?? group.sortOrder.length, 0, elementOrGroup.id);
        if (!group.legacyCustomElements) {
          group.legacyCustomElements = [];
        }
        group.legacyCustomElements.splice(
          targetOrder ?? group.legacyCustomElements.length,
          0,
          elementOrGroup as ApiLegacyCustomElementResponse
        );
      }
      if (!isElement) {
        group.subTransitionStoryGroups = group.subTransitionStoryGroups ?? [];
        group.subTransitionStoryGroups.splice(
          targetOrder ?? group.subTransitionStoryGroups.length,
          0,
          elementOrGroup as ApiTransitionStoryGroupResponse
        );
      }
    }
    if (group.subTransitionStoryGroups?.length) {
      group.subTransitionStoryGroups = _addGroupOrElementRecursively(
        group.subTransitionStoryGroups,
        id,
        elementOrGroup,
        targetGroupId,
        targetOrder
      );
    }

    return [...acc, group];
  }, []);
}

function _updateGroupOrElementRecursively(
  groups: ApiTransitionStoryGroupResponse[],
  id: string,
  title?: string,
  color?: string,
  watchlist?: ApiWatchlistResponse,
  story?: ApiStoryResponse,
  legacyCustomData?: any
) {
  return groups.reduce((acc: ApiTransitionStoryGroupResponse[], group: ApiTransitionStoryGroupResponse) => {
    if (group.id === id) {
      group.title = title ?? group.title;
      group.color = color ?? group.color;
      group.story = story ?? group.story;
    }
    if (group.subTransitionStoryGroups?.length) {
      group.subTransitionStoryGroups = _updateGroupOrElementRecursively(
        group.subTransitionStoryGroups,
        id,
        title,
        color,
        watchlist,
        story,
        legacyCustomData
      );
    }
    if (group.legacyCustomElements?.length) {
      group.legacyCustomElements = group.legacyCustomElements.map((element) => {
        if (element.id === id) {
          let data: string;
          if (legacyCustomData) {
            data = typeof legacyCustomData === 'string' ? legacyCustomData : JSON.stringify(legacyCustomData);
          } else {
            data = typeof element.data === 'string' ? element.data : JSON.stringify(element.data);
          }
          element.title = title ?? element.title;
          element.watchlist = watchlist ?? element.watchlist;
          element.data = data;
          element.story = story ?? element.story;
        }
        return element;
      });
    }
    if (group.transitionElements?.length) {
      group.transitionElements = group.transitionElements.map((element) => {
        if (element.id === id) {
          element.story = story ?? element.story;
        }
        return element;
      });
    }
    return [...acc, group];
  }, []);
}

function _createTransitionHierarchyStories(
  response: ApiListTransitionStoryGroupsResponse,
  baseUrlFiles: string
): Record<string, ApiStoryResponse> {
  const buildStory = (story: ApiStoryResponse): ApiStoryResponse => ({
    ...story,
    imgUrl: story.imgUrl ? conditionallyPrefixImagesWithFilesUrl(story.imgUrl, baseUrlFiles) : '',
  });

  const collectStories = (
    group: ApiTransitionStoryGroupResponse,
    storyMap: { [id: string]: ApiStoryResponse } = {}
  ): { [id: string]: ApiStoryResponse } => {
    // Collect the story from the current group if it exists
    if (group.story) {
      storyMap[group.id] = buildStory(group.story);
    }

    // Collect stories from legacyCustomElements
    group.legacyCustomElements?.forEach((element) => {
      if (element.story) {
        storyMap[element.id] = buildStory(element.story);
      }
    });

    // Collect stories from transitionElements
    group.transitionElements?.forEach((element) => {
      if (element.story) {
        storyMap[element.id] = buildStory(element.story);
      }
    });

    // Recursively collect stories from subTransitionStoryGroups
    group.subTransitionStoryGroups?.forEach((subGroup) => {
      collectStories(subGroup, storyMap);
    });

    return storyMap;
  };
  const transitionHierarchyStories = (response.transitionStoryGroups ?? [])
    .map((group) => collectStories(group))
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  return transitionHierarchyStories;
}

/**
 * Expands URLs for the hierarchy and its elements.
 * This function is used to ensure that all image URLs in the hierarchy are prefixed with the base URL for files if needed.
 * @param hierarchy The hierarchy to expand URLs for (modified in place)
 * @param baseUrlFiles The base URL for files
 * @returns The updated hierarchy with expanded URLs
 */
function _expandUrlsForHierarchy(
  hierarchy: ApiListTransitionStoryGroupsResponse,
  baseUrlFiles: string
): ApiListTransitionStoryGroupsResponse {
  const expandUrls = (group: ApiTransitionStoryGroupResponse): ApiTransitionStoryGroupResponse => {
    if (group.story) {
      group.story.imgUrl = conditionallyPrefixImagesWithFilesUrl(group.story.imgUrl ?? '', baseUrlFiles);
    }
    group.legacyCustomElements?.forEach((element) => {
      if (element.story) {
        element.story.imgUrl = conditionallyPrefixImagesWithFilesUrl(element.story.imgUrl ?? '', baseUrlFiles);
      }
    });
    group.transitionElements?.forEach((element) => {
      if (element.story) {
        element.story.imgUrl = conditionallyPrefixImagesWithFilesUrl(element.story.imgUrl ?? '', baseUrlFiles);
      }
    });
    group.subTransitionStoryGroups?.forEach((subGroup) => {
      expandUrls(subGroup);
    });
    return group;
  };

  hierarchy.transitionStoryGroups?.forEach((group) => {
    expandUrls(group);
  });
  return hierarchy;
}
